import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import api from '../../../../Component/axios_Interceptor/api';
import { SERVER_ADDRESS } from '../../../../Component/server';

type Account = { bank: string; account: string; holder: string; version: number };
// Bank names checked against KFTC participants and KCP bank codes (2026-11-02).
// https://openapi.kftc.or.kr/service/openBanking · https://developer.kcp.co.kr/code/bank
const refundBanks = ['KB국민은행','신한은행','우리은행','하나은행','NH농협은행','IBK기업은행',
  '카카오뱅크','케이뱅크','토스뱅크','KDB산업은행','SC제일은행','한국씨티은행','Sh수협은행',
  'iM뱅크','BNK부산은행','광주은행','제주은행','전북은행','BNK경남은행',
  '지역농·축협','수협중앙회','신협','MG새마을금고','우체국','저축은행','산림조합'];
const empty = { bank: '', account: '', holder: '', version: 0 };
export function useRefundAccount(enabled = true) {
  const [value, setValue] = useState<Account>(empty);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState('');
  const revision = useRef(0);
  const load = async () => {
    const current = ++revision.current;
    setReady(false); setError('');
    try {
      const { data } = await api.get(`${SERVER_ADDRESS}user/refund-account`);
      if (current !== revision.current) return;
      if (!data || !Object.prototype.hasOwnProperty.call(data, 'refund_account')) throw new Error('Invalid account response');
      setValue(data.refund_account || empty); setReady(true);
    } catch { if (current === revision.current) setError('환불 계좌를 불러오지 못했습니다. 다시 조회해주세요.'); }
  };
  useEffect(() => { if (enabled) void load(); return () => { revision.current++; }; }, [enabled]); // eslint-disable-line react-hooks/exhaustive-deps
  const payload = () => {
    if (!ready) throw new Error('환불 계좌를 먼저 불러와주세요.');
    const account = value.account.replace(/[\s-]/g, '');
    if (!value.version && !value.bank.trim() && !account && !value.holder.trim()) return undefined;
    if (!value.bank.trim() || !value.holder.trim() || !/^[0-9]{5,30}$/.test(account)) throw new Error('은행·예금주와 5~30자리 숫자 계좌번호를 모두 입력해주세요.');
    return { bank: value.bank.trim(), account, holder: value.holder.trim(), expected_version: value.version };
  };
  return {value,ready,error,load,payload,apply:(account:Account|undefined)=>{if(account)setValue(account);},
    update:(key:'bank'|'account'|'holder',text:string)=>setValue(previous=>({...previous,[key]:text}))};
}
export default function RefundAccountFields({state,disabled=false,prefix='refund'}:{state:ReturnType<typeof useRefundAccount>;disabled?:boolean;prefix?:string}) {
  return <Section aria-labelledby={`${prefix}-title`}>
    <h2 id={`${prefix}-title`}>무통장 입금 환불 정보</h2>
    <p>무통장 입금 환불을 받을 본인 명의 계좌를 등록해주세요.</p>
    {state.ready ? <Fields>
      <label htmlFor={`${prefix}-bank`}>은행</label>
      <select id={`${prefix}-bank`} value={state.value.bank} disabled={disabled} onChange={event=>state.update('bank',event.target.value)}>
        <option value="">은행을 선택해주세요</option>
        {state.value.bank && !refundBanks.includes(state.value.bank) && <option value={state.value.bank}>{state.value.bank} (기존 등록)</option>}
        {refundBanks.map(bank=><option key={bank} value={bank}>{bank}</option>)}
      </select>
      <label htmlFor={`${prefix}-account`}>계좌번호</label>
      <input id={`${prefix}-account`} value={state.value.account} maxLength={40} disabled={disabled} inputMode="numeric" autoComplete="off" onChange={event=>state.update('account',event.target.value)}/>
      <label htmlFor={`${prefix}-holder`}>예금주</label>
      <input id={`${prefix}-holder`} value={state.value.holder} maxLength={100} disabled={disabled} autoComplete="off" onChange={event=>state.update('holder',event.target.value)}/>
    </Fields> : !state.error && <p role="status">환불 계좌를 불러오는 중입니다.</p>}
    {state.error && <><p role="alert">{state.error}</p><button type="button" disabled={disabled} onClick={()=>void state.load()}>환불 계좌 다시 조회</button></>}
  </Section>;
}
const Section=styled.section`
  padding-top:24px; border-top:1px solid var(--user-border);
  h2 {margin:0 0 12px;font-size:18px;} p {font-size:14px;line-height:1.6;color:var(--user-text-muted);}
  button {min-height:44px;padding:10px 16px;border:1px solid var(--user-border);border-radius:8px;background:var(--user-button);color:#fff;font:inherit;cursor:pointer;}
  button:focus-visible {outline:2px solid var(--user-accent);outline-offset:2px;} [role="alert"] {color:var(--user-error);}
`;
const Fields=styled.div`
  display:grid;gap:10px;label {margin-top:6px;font-size:14px;color:var(--user-text);}
  input, select {width:100%;box-sizing:border-box;min-height:48px;padding:12px;font:inherit;color:var(--user-text);background:var(--user-surface-raised);border:1px solid var(--user-border);border-radius:8px;}
  select {appearance:auto;}
  input:focus-visible, select:focus-visible {outline:2px solid var(--user-accent);outline-offset:2px;}
`;
